import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Inject,
  OnDestroy,
  OnInit,
  ViewChild,
  ViewContainerRef
} from "@angular/core";
import { CommonModule, NgOptimizedImage } from "@angular/common";
import {
  confirmationModalServiceInjectionToken,
  EPageLayout,
  ESeverity,
  IConfirmationModalService,
  IToastService,
  toastServiceInjectionToken,
  IToggleButton,
  PageLayoutComponent,
  IconComponent,
  ToggleButtonComponent,
  CardComponent,
  CardGroupComponent
} from "@zaeper/angular-dashboard-view-lib";
import { ZDictionaryPipe } from "@zaeper/angular-localization-lib";
import { ZTemplateDirective } from "@zaeper/angular-core-lib";
import { ButtonModule } from "primeng/button";
import { SelectButton } from "primeng/selectbutton";
import { TooltipModule } from "primeng/tooltip";
import { CardModule } from "primeng/card";
import { Tag } from "primeng/tag";
import { MenuItem } from "primeng/api";
import {
  connectReceiverModalServiceInjectionToken
} from "../../modals/connect-receiver-modal/connect-receiver-modal-service-injection-token";
import {IConnectReceiverModalService} from "../../modals/connect-receiver-modal/i-connect-receiver-modal.service";
import {livekitClientInjectionToken} from "../../services/livekit/livekit-client-injection-token";
import {ILivekitClient} from "../../services/livekit/i-livekit.client";
import {sessionStateServiceInjectionToken} from "../../global-states/session/session-state-service-injection-token";
import {ISessionStateService} from "../../global-states/session/i-session-state.service";
import {BehaviorSubject, combineLatest, filter, map, mergeWith, Observable, Subscription} from "rxjs";
import {IReceiver} from "../../models/i-receiver";
import {IGroup} from "../../models/i-group";
import {IConnectReceiverModalData} from "../../modals/connect-receiver-modal/i-connect-receiver-modal-data";
import {
  scheduleReceiverConfigPushModalServiceInjectionToken
} from "../../modals/schedule-receiver-config-push-modal/schedule-receiver-config-push-modal-service-injection-token";
import {
  IScheduleReceiverConfigPushModalService
} from "../../modals/schedule-receiver-config-push-modal/i-schedule-receiver-config-push-modal.service";
import {
  applicationsOverviewModalServiceInjectionToken
} from "../../modals/applications-overview-modal/applications-overview-modal-service-injection-token";
import {
  IApplicationsOverviewModalService
} from "../../modals/applications-overview-modal/i-applications-overview-modal.service";
import {receiverWsClientInjectionToken} from "../../ws-clients/receiver/receiver-ws-client-injection-token";
import {IReceiverWsClient} from "../../ws-clients/receiver/i-receiver-ws.client";
import {IConnectionStateMessage} from "../../ws-clients/receiver/messages/i-connection-state-message";
import {LiveViewContainerComponent} from "../../components/live-view-container/live-view-container.component";
import {PreviewContainerComponent} from "../../components/preview-container/preview-container.component";
import {StatisticCardComponent} from "./components/statistic-card/statistic-card.component";
import {OnlineStatusIndicatorComponent} from "./components/online-status-indicator/online-status-indicator.component";
import {ReceiverListItemComponent} from "./components/receiver-list-item/receiver-list-item.component";
import {ReceiverPlaceholderComponent} from "./components/receiver-placeholder/receiver-placeholder.component";
import {
  fullscreenPreviewModalServiceInjectionToken
} from "../../modals/fullscreen-preview-modal/fullscreen-preview-modal-service-injection-token";
import {IFullscreenPreviewModalService} from "../../modals/fullscreen-preview-modal/i-fullscreen-preview-modal.service";
import {GtmService} from "../../services/gtm/gtm.service";

enum EReceiverViewMode {
  PREVIEW = 0,
  LIVE_VIEW = 1
}

@Component({
  selector: "cms-receiver-previewer-page",
  templateUrl: "receiver-previewer-page.component.html",
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrls: ["./receiver-previewer-page.component.scss"],
  standalone: true,
  imports: [
    CommonModule,
    ButtonModule,
    TooltipModule,
    ZDictionaryPipe,
    ZTemplateDirective,
    PageLayoutComponent,
    ToggleButtonComponent,
    LiveViewContainerComponent,
    PreviewContainerComponent,
    CardModule,
    IconComponent,
    CardComponent,
    CardGroupComponent,
    StatisticCardComponent,
    OnlineStatusIndicatorComponent,
    ReceiverListItemComponent,
    ReceiverPlaceholderComponent,
    Tag
  ]
})
export class ReceiverPreviewerPageComponent implements OnInit, AfterViewInit, OnDestroy {
  public readonly receiverViewMode$: Observable<EReceiverViewMode>;
  public readonly eReceiverViewMode: typeof EReceiverViewMode = EReceiverViewMode;
  public readonly ePageLayout: typeof EPageLayout = EPageLayout;
  public readonly isReceiverOnline$: Observable<boolean>;
  public readonly activeReceiver$: Observable<IReceiver | null>;
  public readonly activeGroup$: Observable<IGroup | null>;
  public readonly receivers$: Observable<IReceiver[]>;
  public readonly toggleButtons: IToggleButton[];
  public readonly navigation: MenuItem[];
  private readonly _subscriptions: Subscription = new Subscription();
  private readonly _receiverViewMode$: BehaviorSubject<EReceiverViewMode>;

  // Statistics properties (placeholder values - replace with real data when available)
  public uptime: string = "12h 34m";
  public lastSync: string = "vor 5m";
  public cpuUsage: number = 23;
  public memoryUsage: number = 45;
  public storageUsage: number = 67;
  public networkStatus: string = "Stabil";
  public installedApps: number = 8;
  public scheduledTasks: number = 3;
  public warnings: number = 0;
  public healthStatus: string = "Gut";


  @ViewChild("previewerFrame", {read: ElementRef}) private readonly _previewerFrame!: ElementRef;
  @ViewChild("previewerFrameWrapper", {read: ElementRef}) private readonly _previewerFrameWrapper!: ElementRef;
  @ViewChild("previewContainer", {read: ViewContainerRef}) private readonly _previewContainer!: ViewContainerRef
  @ViewChild("liveViewContainer", {read: ViewContainerRef}) private readonly _liveViewContainer!: ViewContainerRef

  constructor(
    @Inject(connectReceiverModalServiceInjectionToken) private readonly _connectReceiverModalService: IConnectReceiverModalService,
    @Inject(livekitClientInjectionToken) private readonly _livekitClient: ILivekitClient,
    @Inject(sessionStateServiceInjectionToken) private readonly _sessionStateService: ISessionStateService,
    @Inject(confirmationModalServiceInjectionToken) private readonly _confirmationModalService: IConfirmationModalService,
    @Inject(toastServiceInjectionToken) private readonly _toastService: IToastService,
    @Inject(scheduleReceiverConfigPushModalServiceInjectionToken) private readonly _scheduleReceiverConfigPushModalService: IScheduleReceiverConfigPushModalService,
    @Inject(applicationsOverviewModalServiceInjectionToken) private readonly _applicationsOverviewModalService: IApplicationsOverviewModalService,
    @Inject(receiverWsClientInjectionToken) private readonly _receiverWsClient: IReceiverWsClient,
    @Inject(fullscreenPreviewModalServiceInjectionToken) private readonly _fullscreenPreviewModalService: IFullscreenPreviewModalService,
    private readonly _changeDetectorRef: ChangeDetectorRef,
    private readonly _gtmService: GtmService
  ) {
    this.activeReceiver$ = this._sessionStateService.activeReceiver$;
    this.activeGroup$ = this._sessionStateService.activeGroup$;
    this.receivers$ = this._sessionStateService.quickAccessReceivers$;
    this.isReceiverOnline$ = this.activeReceiver$.pipe(map((receiver: IReceiver | null) => !!receiver?.isOnline)).pipe(mergeWith(this._receiverWsClient.getConnectionState$().pipe(map((connectionState: IConnectionStateMessage) => connectionState.connected))));
    this._receiverViewMode$ = new BehaviorSubject<EReceiverViewMode>(EReceiverViewMode.PREVIEW)
    this.receiverViewMode$ = this._receiverViewMode$.asObservable();
    this.toggleButtons = [
      {
        label: "Vorschau",
        iconName: "web_asset"
      },
      {
        label: "Live-Ansicht",
        iconName: "radio_button_checked"
      }
    ];
    this.navigation = [
      {
        icon: 'pi pi-home',
        routerLink: '/de/',
      },
      {
        label: 'Receiver Dashboard',
        routerLink: '/de/receiver-dashboard',
      }
    ];
  }

  ngAfterViewInit(): void {
    this._handleReceiverViewModeChange();
    this._handleActiveReceiverChange();
  }

  ngOnInit() {
  }

  async ngOnDestroy(): Promise<void> {
    this._subscriptions.unsubscribe();
    await this._livekitClient.disconnect();
  }

  public openConnectReceiverModal() {
    const cloudId: string | undefined = this._sessionStateService.getActiveCloud()?.id;
    const groupId: string | undefined = this._sessionStateService.getActiveGroup()?.id;

    if (cloudId && groupId) {
      const connectReceiverModalData: IConnectReceiverModalData = {
        cloudId, groupId
      };

      this._connectReceiverModalService.open(connectReceiverModalData);
    }
  }

  public openApplicationsOverviewModal(): void {
    const cloudId: string | undefined = this._sessionStateService.getActiveCloud()?.id;
    const groupId: string | undefined = this._sessionStateService.getActiveGroup()?.id;
    const receiver: IReceiver | null = this._sessionStateService.getActiveReceiver();

    if (cloudId && groupId && receiver) {
      this._applicationsOverviewModalService.openApplicationsOverviewModal({
        cloudId, groupId, receiver
      });
    }
  }

  public openScheduleReceiverConfigPushModal(): void {
    this._scheduleReceiverConfigPushModalService.open();
  }

  public openPushReceiverConfigModal(): void {
    this._confirmationModalService.open({
      title: "Konfigurationen laden",
      description: "Soll der Receiver die zurzeit konfigurierten Einstellungen laden?",
      confirmationText: "Laden",
      onConfirm: () => {
        this._toastService.setMessage({
          title: "Erfolgreich",
          detail: "Befehl zum Laden der Einstellungen wurde erfolgreich versendet.",
          severity: ESeverity.success
        });
        return Promise.resolve();
      }
    });
  }

  public switchViewMode(activeIndex: number) {
    this._gtmService.pushEvent( "receiver_view_mode_switch", { view_mode: activeIndex === EReceiverViewMode.PREVIEW ? "preview" : "live_view" } );
    this._receiverViewMode$.next(activeIndex);
  }

  public openFullscreenPreview(): void {
    this._fullscreenPreviewModalService.open();
  }

  private _handleActiveReceiverChange() {
    this._subscriptions.add(this._sessionStateService.activeReceiver$.subscribe(() => {
      const receiver: IReceiver | null = this._sessionStateService.getActiveReceiver();
      const resolutionX: number | undefined = receiver?.resolutionX;
      const resolutionY: number | undefined = receiver?.resolutionY;

      this._changeDetectorRef.detectChanges();
      if (resolutionX && resolutionY) {
        this._previewerFrameWrapper.nativeElement.style.aspectRatio = `${resolutionX}/${resolutionY}`;
        this._previewerFrame.nativeElement.style.aspectRatio = `${resolutionX}/${resolutionY}`;
      } else {
        this._previewerFrameWrapper.nativeElement.style.aspectRatio = '';
        this._previewerFrame.nativeElement.style.aspectRatio = '';
      }
    }));
  }

  private _handleReceiverViewModeChange() {
    this._subscriptions.add(
      combineLatest([this._receiverViewMode$, this._sessionStateService.activeReceiver$])
        .pipe(filter(([, receiver]: [EReceiverViewMode, IReceiver | null]) => receiver !== null))
        .subscribe(([receiverViewMode]: [EReceiverViewMode, IReceiver | null]) => {
          this._changeDetectorRef.detectChanges();
          if (receiverViewMode === EReceiverViewMode.PREVIEW && this._previewContainer && this._previewContainer.length === 0) {
            this._previewContainer.createComponent(PreviewContainerComponent);
          }
          if (receiverViewMode === EReceiverViewMode.LIVE_VIEW && this._liveViewContainer && this._liveViewContainer.length === 0) {
            this._liveViewContainer.createComponent(LiveViewContainerComponent);
          }
          this._changeDetectorRef.detectChanges();
        })
    )
  }

  public switchToReceiver(receiver: IReceiver): void {
    this._sessionStateService.setActiveReceiver(receiver);
  }
}
